import { FC, ReactNode } from "react";
import { Pressable } from "react-native";
import { useSetAtom } from "jotai";
import { XIcon } from "lucide-react-native";

import { ApplicationTypeEnumType } from "@libs/api/patients";
import { VnCard } from "@/components/VnCard";
import { VnIcon } from "@/components/VnIcon";
import { VnText } from "@/components/VnText/VnText";
import { getApplicantFirstNames } from "@/domain/applicationApplicants";
import {
  DashboardCardType,
  getSubmissionConfirmationDismissalId,
} from "@/domain/dashboardCards";
import { useTranslator } from "@/hooks/useTranslate";
import { dismissedDashboardCardIdsAtom } from "@/stores/atoms";
import { formatMemberList, getProgramTypeLabel } from "@/utils/brandLabel";

type ApplicationSubmittedCardVariantType = Extract<
  DashboardCardType,
  { kind: "APPLICATION_SUBMITTED" }
>;

type ApplicationSubmittedCardPropsType = {
  card: ApplicationSubmittedCardVariantType;
};

// Programs are named in a fixed order so the sentence stays stable regardless
// of the order the submitted applications come back in.
const PROGRAM_ORDER: ApplicationTypeEnumType[] = [
  "MEDICAID",
  "MEDICARE",
  "MARKETPLACE",
];

export const ApplicationSubmittedCard: FC<
  ApplicationSubmittedCardPropsType
> = ({ card }) => {
  const { t } = useTranslator();
  const setDismissedCardIds = useSetAtom(dismissedDashboardCardIdsAtom);

  const { applications } = card;

  const programs = PROGRAM_ORDER.filter(programType =>
    applications.some(
      application => application.applicationType === programType,
    ),
  ).map(getProgramTypeLabel);
  const programList = programs.flatMap<ReactNode>((program, index) => [
    index === 0
      ? ""
      : index === programs.length - 1
        ? t("common:listConnector")
        : t("common:listSeparator"),
    <VnText key={program} variant="lg" className="font-primary-semibold">
      {program}
    </VnText>,
  ]);

  const memberNames = getApplicantFirstNames(
    applications.flatMap(application => application.applicants),
  );

  const isSingleMarketplaceApplication =
    applications.length === 1 &&
    applications[0]?.applicationType === "MARKETPLACE";
  const summaryI18nKey = isSingleMarketplaceApplication
    ? "native:homeScreen.applicationSubmittedCard_singleMarketplace"
    : applications.length === 1
      ? "native:homeScreen.applicationSubmittedCard_single"
      : "native:homeScreen.applicationSubmittedCard_multiple";

  const handleDismiss = () => {
    const dismissalIds = applications.map(application =>
      getSubmissionConfirmationDismissalId(application.id),
    );

    setDismissedCardIds(previous => [
      ...new Set([...previous, ...dismissalIds]),
    ]);
  };

  return (
    <VnCard className="flex flex-row items-center gap-4">
      <VnText
        variant="lg"
        className="flex-1"
        i18nKey={summaryI18nKey}
        i18nOptions={{
          memberNames: formatMemberList({ names: memberNames }),
        }}
        i18nProps={{
          components: {
            programs: <VnText variant="lg">{programList}</VnText>,
          },
        }}
      />

      <Pressable
        accessibilityRole="button"
        accessibilityLabel={t("common:close")}
        hitSlop={12}
        onPress={handleDismiss}>
        <VnIcon as={XIcon} />
      </Pressable>
    </VnCard>
  );
};
